import { useCallback } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';

import MapPinIcon from '@/components/Icons/MapPinIcon';
import MapPinnedIcon from '@/components/Icons/MapPinnedIcon';
import { MAX_PINNED_IDS } from '@/constants';
import useUser from '@/hooks/useUser';
import { InferenceVolumeSharedEndpointInstantDocument } from '@/sections/Workspace/Metrics/Tabs/__generated__/queries.generated';
import { useMetricQuery } from '@/sections/Workspace/Metrics/hooks';
import { useSnackbar } from 'notistack';
import { useRecoilState } from 'recoil';

import { pinnedIdsState } from '../atoms';
import { WorkspaceSections } from '../types';
import { sortPinnedIds } from '../utils';
import { useWorkspaceOrgSharedEndpointsQuery } from './__generated__/queries.generated';

const POLL_INTERVAL = 15000;

function useOrgSharedEndpoints(poll: boolean = false) {
  const { data, loading } = useWorkspaceOrgSharedEndpointsQuery({
    fetchPolicy: 'cache-first',
    pollInterval: poll ? POLL_INTERVAL : null,
  });

  return {
    orgSharedEndpoints: data?.organizationSharedEndpoints || [],
    loading,
  };
}

function useOrgSharedEndpointsInferenceVolume(id: string, isVisible = true) {
  const { data, loading } = useMetricQuery({
    document: InferenceVolumeSharedEndpointInstantDocument,
    isVisible,
    isSharedEndpoint: true,
    isTrainingJob: false,
    providerId: id,
    timeDivisorSeconds: 3600, // Use 1 hour (3600 seconds) as time divisor to show requests per hour
  });

  return {
    data,
    loading,
  };
}

function usePinnedAPI() {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [pinnedIds, setPinnedIds] = useRecoilState(
    pinnedIdsState(`${WorkspaceSections.ModelAPIs}-${user.id}`),
  );
  const { orgSharedEndpoints, loading } = useOrgSharedEndpoints();

  const pinnedEndpoints = orgSharedEndpoints
    .filter((endpoint) => pinnedIds.includes(endpoint.id))
    .sort((a, b) => sortPinnedIds(pinnedIds, a.id, b.id));

  const togglePinEntity = useCallback(
    (entityId: string, endpointName: string) => {
      setPinnedIds((prev) => {
        if (prev.includes(entityId)) {
          return prev.filter((id) => id !== entityId);
        }
        enqueueSnackbar(`Pinned ${endpointName} to the sidebar`, { variant: 'success' });
        return [entityId, ...prev];
      });
    },
    [setPinnedIds, enqueueSnackbar],
  );

  const canPinMoreEndpoints = pinnedEndpoints.length < MAX_PINNED_IDS;

  function stopPropagation(ev: React.MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
  }

  function getPinActionItem(entityId: string, endpointName: string) {
    const isPinned = pinnedIds.includes(entityId);
    const isPinDisabled = !canPinMoreEndpoints && !isPinned;

    return (
      <Tooltip
        title={
          isPinDisabled
            ? `Limit ${MAX_PINNED_IDS} pins`
            : isPinned
            ? 'Unpin from sidebar'
            : 'Pin to sidebar'
        }
      >
        <Box>
          <IconButton
            onClick={(e) => {
              stopPropagation(e);
              togglePinEntity(entityId, endpointName);
            }}
            data-cy="pin-entity-btn"
            disabled={isPinDisabled}
          >
            {isPinned ? <MapPinnedIcon color="inherit" /> : <MapPinIcon color="secondary" />}
          </IconButton>
        </Box>
      </Tooltip>
    );
  }

  return {
    pinnedIds,
    togglePinEntity,
    canPinMoreEndpoints,
    getPinActionItem,
    loading,
    pinnedEndpoints,
  };
}

export { useOrgSharedEndpoints, usePinnedAPI, useOrgSharedEndpointsInferenceVolume };
